'use client';

import { format } from 'date-fns';
import { Fragment, forwardRef, useCallback, useContext, useMemo } from 'react';

import { cx } from '@/styled-system/css';

import { Text } from '../text/text';
import { ToggleCard } from '../toggle-card/toggle-card';
import { DateTimeBlockRootContext } from './context/date-time-block-root.context';
import { useDateTimeBlockDisabledItems } from './hooks/use-disabled-items';
import { dateTimeBlockDayRecipe } from './styles/date-time-block-day.styles';
import type { DateTimeBlockDayPropsInterface } from './types/date-time-block-day.types';

/**
 * `DateTimeBlock.Day` represents the day to be selected
 *
 * @since 10.2.0
 */
export const DateTimeBlockDay = forwardRef<HTMLDivElement, DateTimeBlockDayPropsInterface>(
  (props: DateTimeBlockDayPropsInterface, forwardedRef) => {
    const { children, css: cssProp, date, slideIndex, totalSlides, ...rest } = props;

    const { selectedDate, onDateChange, disabledDates } = useContext(DateTimeBlockRootContext);

    const isDisabled = useDateTimeBlockDisabledItems(disabledDates);

    const handleDateChange = useCallback(() => {
      if (onDateChange) {
        onDateChange(date);
      }
    }, [onDateChange, date]);

    const isDateSelected = useCallback(
      (date: Date | string) => {
        if (!selectedDate) return false;
        if (date instanceof Date && selectedDate instanceof Date) {
          return date.getTime() === selectedDate.getTime();
        }
        return date === selectedDate;
      },
      [selectedDate],
    );

    const formattedAriaLabel = useMemo(() => format(date, 'eeee, MMMM d'), [date]);
    const formattedDay = useMemo(() => format(date, 'EEE'), [date]);
    const formattedDate = useMemo(() => format(date, 'MMM d'), [date]);
    const formattedSlideAriaLabel = useMemo(() => {
      if (slideIndex === undefined || totalSlides === undefined || totalSlides <= 0) {
        return formattedAriaLabel;
      }

      return `${formattedAriaLabel}, Date ${slideIndex + 1} of ${totalSlides}`;
    }, [formattedAriaLabel, slideIndex, totalSlides]);

    return (
      <ToggleCard
        ref={forwardedRef}
        data-c11n-component="DateTimeBlock.Day"
        data-c11n-version={__C11N_VERSION__}
        disabled={isDisabled(date)}
        aria-label={formattedSlideAriaLabel}
        aria-roledescription="slide"
        elevated={false}
        css={{ ...dateTimeBlockDayRecipe, ...cssProp }}
        body={
          <Fragment>
            <Text textStyle="heading-xs">{formattedDay}</Text>
            <Text textStyle="body-sm">{formattedDate}</Text>
          </Fragment>
        }
        onClick={() => !isDisabled(date) && handleDateChange()}
        outlined
        selected={isDateSelected(date)}
        tone="neutral"
        {...rest}
        className={cx('c11n_date-time-block__day', rest.className)}
      >
        {children}
      </ToggleCard>
    );
  },
);

DateTimeBlockDay.displayName = 'DateTimeBlock.Day';
